import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useShallow } from "zustand/react/shallow";
import { useUIStore } from "../stores/uiStore";
import { useTelemetryStore, assetShownOnSite } from "../stores/telemetryStore";
import { useLayoutStore } from "../stores/layoutStore";
import { useSiteStore, ROLE_RANK } from "../stores/siteStore";
import { useLiveDataAvailability } from "../hooks/useLiveDataAvailability";
import { api, putWarehouseTransformById } from "../api/rest";
import { RunSelectionSection } from "./RunSelectionSection";
import { useCollapsedPanel } from "../hooks/useCollapsedPanel";
import { Reveal, type Requirement } from "../components/Reveal";
import { AGING_BUCKETS, EXPIRY_BUCKETS, agingFinderSeed, bucketFinderSeed } from "../scene/expiryPaint";
import { WMS_CLASSES } from "../scene/wmsPaint";
import styles from "./Panel.module.css";

/**
 * The /live layer panel, grouped into layer CLASSES (ADR 192, design §2):
 *
 * - **Ativos** — physical entities with live position/state. Fleet rows are
 *   GENERATED from the asset types present in the roster (a pose's `series`
 *   carries its typeCode) — adding a new asset type needs no code change here.
 *   Racks + pallet loads join this class on inventory-capable sites.
 * - **Análises** — map-rendered analytics (heatmap, spaghetti, trails). RTLS
 *   data, so the whole class hides on a site without that capability.
 * - **KPIs** — on-map per-aisle KPI badges (ADR 193): coverage and occupancy,
 *   backed by the same `structure/occupancy` rollup the report panels read.
 *   Inventory-gated — the values only exist where runs do.
 * - **Referência** — zones, labels, the 2D minimap.
 *
 * ADR 227 — a row appears only when BOTH its capability is on and its data
 * exists. The earlier rule ("an empty class row costs nothing but a checkbox")
 * was wrong: on a freshly imported warehouse it offered "Paletes e cargas" and
 * the whole KPI section with no run behind them, and "Planta 2D" for a plan
 * nobody had uploaded — controls bound to nothing, the same defect as a badge
 * that claims a connection it does not have (ADR 224). Availability comes from
 * cheap counts (`useLiveDataAvailability`), never the layer payloads.
 */
export function LayerPanel() {
  const { t, i18n } = useTranslation();
  const layers      = useUIStore((s) => s.layers);
  const toggle      = useUIStore((s) => s.toggleLayer);
  const hiddenTypes = useUIStore((s) => s.hiddenAssetTypes);
  const toggleType  = useUIStore((s) => s.toggleAssetType);
  const preset      = useUIStore((s) => s.cameraPreset);
  const setPreset   = useUIStore((s) => s.setCameraPreset);
  const walkMode    = useUIStore((s) => s.walkMode);
  const setWalkMode = useUIStore((s) => s.setWalkMode);
  const deselect    = useTelemetryStore((s) => s.select);
  const editMode    = useUIStore((s) => s.editMode);
  const toggleEdit  = useUIStore((s) => s.toggleEditMode);
  // ADR 354 paint-by mode + legend tallies (written by the occupancy container).
  const paintBy     = useUIStore((s) => s.paintBy);
  const setPaintBy  = useUIStore((s) => s.setPaintBy);
  const paintCounts = useUIStore((s) => s.paintCounts);
  const setProductFilters = useUIStore((s) => s.setProductFilters);
  // Same one-date-per-mount idiom as the occupancy container: runs are batch,
  // and any remount corrects a midnight rollover.
  const [todayIso] = useState(() => new Date().toISOString().slice(0, 10));
  const [collapsed, toggleCollapsed] = useCollapsedPanel("layers");
  const beginEdit   = useLayoutStore((s) => s.beginEdit);
  const commitEdit  = useLayoutStore((s) => s.commitEdit);
  const cancelEdit  = useLayoutStore((s) => s.cancelEdit);
  const pending     = useLayoutStore((s) => s.pendingTransform);
  const activeSiteId = useSiteStore((s) => s.activeSiteId);
  const activeWarehouseId = useSiteStore((s) => s.activeWarehouseId);
  const hasInventory = useSiteStore((s) => s.hasCapability("inventory"));
  const hasRtls      = useSiteStore((s) => s.hasCapability("rtls"));
  const canEditLayout = useSiteStore((s) => s.isGlobalAdmin || s.roleRank >= ROLE_RANK["site-admin"]);
  // ADR 227: capability says "this site may show racks"; availability says "this
  // warehouse actually has some". A row needs both.
  const available = useLiveDataAvailability(activeSiteId, activeWarehouseId);
  // ADR 324: an in-progress run streaming into THIS warehouse. Read straight off the
  // live store rather than a query — there is nothing stored to ask about.
  const liveInventory = useTelemetryStore((s) => {
    const a = s.inventoryActivity;
    return !!a && a.siteId === activeSiteId
      && (!a.warehouseId || a.warehouseId === activeWarehouseId);
  });
  // ADR 363: the row names how far the sweep has got — the number the overlay paints.
  const liveVisited = useTelemetryStore((s) => (liveInventory && s.inventoryActivity ? s.inventoryActivity.visited.size : 0));

  // Distinct asset types present in the fleet. useShallow: pose deltas rewrite
  // the forklifts record ~11 Hz per asset, but the TYPE LIST almost never
  // changes — shallow-comparing the derived array keeps the panel from
  // re-rendering on every delta.
  const presentTypes = useTelemetryStore(
    useShallow((s) => {
      const codes = new Set<string>();
      // ADR 394: with RTLS off the roster is not a fleet — no rows, no obsoletos modifier.
      for (const p of Object.values(s.forklifts)) {
        if (assetShownOnSite(hasRtls, s.gateways, p.id)) codes.add(p.series);
      }
      return [...codes].sort();
    }),
  );

  // Type labels are user-authored data (asset_type.label), not i18n copy.
  const { data: assetTypes } = useQuery({
    queryKey: ["asset-types", activeSiteId],
    queryFn: () => api.listAssetTypes(activeSiteId),
    enabled: !!activeSiteId && presentTypes.length > 0,
    staleTime: 60_000,
  });
  const labelOf = (code: string) =>
    assetTypes?.find((at) => at.typeCode === code)?.label ?? code;

  // ADR 356: each gate's requirement vocabulary, once. A row/class renders
  // through Reveal — met requirements pass the row through, missing ones
  // become the diagnostic ghost (or nothing with the mode off).
  const invReq: Requirement = { label: t("req.inventoryCapability"), ok: hasInventory };
  const rtlsReq: Requirement = { label: t("req.rtlsCapability"), ok: hasRtls };
  const runsReq: Requirement = { label: t("req.runs"), ok: !!available.runs };
  const wmsReq: Requirement = { label: t("req.wms"), ok: !!available.wms };
  const verticalReq: Requirement = { label: t("req.vertical"), ok: !!available.vertical };
  // Validade, Idade and WMS all paint a WMS fact (ADR 354/375): without a snapshot the
  // control is hidden with the KPI badges (ADR 410 rule), and a mode carried over from
  // another warehouse falls back to Status so the hidden paint cannot stay selected.
  useEffect(() => {
    if (!available.loading && !available.wms && paintBy !== "status") setPaintBy("status");
  }, [available.loading, available.wms, paintBy, setPaintBy]);

  const layerRow = (k: keyof typeof layers, label: string) => (
    <label className={styles.row}>
      <input type="checkbox" checked={layers[k]} onChange={() => toggle(k)} aria-label={label} />
      <span className={styles.label}>{label}</span>
    </label>
  );

  const handleToggleEdit = () => {
    if (!editMode) {
      beginEdit();
      toggleEdit();
    }
  };

  const handleSave = () => {
    // Explicit-warehouse rule (retrospective C5): the transform PUT targets the
    // store's real warehouse (the legacy path invented wh-{siteId} and wrote
    // the camelCase SiteTransform shape instead of the API's snake_case one).
    if (!pending || !activeSiteId || !activeWarehouseId) return;
    putWarehouseTransformById(activeSiteId, activeWarehouseId, {
      origin_offset: [pending.originOffsetX, 0, pending.originOffsetZ],
      rotation_y_rad: pending.rotationYRad,
      scale: pending.scale,
    })
      .then(() => { commitEdit(); toggleEdit(); })
      .catch(() => { commitEdit(); toggleEdit(); });
  };

  const handleCancel = () => {
    cancelEdit();
    toggleEdit();
  };

  return (
    <aside className={styles.panel} aria-label={t("a11y.layers")}>
      <h2 className={`${styles.title} ${collapsed ? styles.titleCollapsed : ""}`}>
        <button type="button" className={styles.titleBtn} aria-expanded={!collapsed} onClick={toggleCollapsed}>
          {t("layers.title")}
          <span className={styles.caret} aria-hidden>{collapsed ? "▸" : "▾"}</span>
        </button>
      </h2>
      {!collapsed && (<>

      <h3 className={styles.classTitle}>{t("layers.class.assets")}</h3>
      {presentTypes.map((code) => (
        <label key={code} className={styles.row}>
          <input
            type="checkbox"
            checked={!hiddenTypes[code]}
            onChange={() => toggleType(code)}
            aria-label={labelOf(code)}
          />
          <span className={styles.label}>{labelOf(code)}</span>
        </label>
      ))}
      <Reveal label={t("layers.staleRobots")} reqs={[{ label: t("req.fleet"), ok: presentTypes.length > 0 }]}>
        {layerRow("staleRobots", t("layers.staleRobots"))}
      </Reveal>
      {/* ADR 204: same capability rule the Análises class already follows —
          sensors are RTLS hardware, alert beams need a telemetry/RTLS pipeline. */}
      <Reveal label={t("layers.sensors")} reqs={[rtlsReq]}>{layerRow("sensors", t("layers.sensors"))}</Reveal>
      {/* ADR 282: beams need an asset with a live pose, so the row is RTLS-gated;
          inventory alerts surface on /alerts and the widgets, never as beams. */}
      <Reveal label={t("layers.alerts")} reqs={[rtlsReq]}>{layerRow("alerts", t("layers.alerts"))}</Reveal>
      <Reveal label={t("layers.racks")} reqs={[invReq, { label: t("req.racks"), ok: !!available.racks }]}>
        {layerRow("rackVolumes", t("layers.racks"))}
      </Reveal>
      {/* Pallet loads come from a counted run — with none imported there is nothing
          to paint, and the toggle would be inert. */}
      <Reveal label={t("layers.pallets")} reqs={[invReq, runsReq]}>
        {layerRow("occupancy", t("layers.pallets"))}
      </Reveal>
      {/* ADR 324: only while a run is actually streaming. Unlike every other row this one
          is gated on live traffic, not on stored data — a checkbox for a sweep nobody is
          running would toggle nothing and read as a broken feature. */}
      <Reveal label={t("layers.liveInventory")} reqs={[invReq, { label: t("req.liveRun"), ok: liveInventory }]}>
        {layerRow("liveInventory", liveVisited > 0
          ? `${t("layers.liveInventory")} · ${liveVisited.toLocaleString(i18n.language)}`
          : t("layers.liveInventory"))}
      </Reveal>

      <Reveal label={t("layers.class.analytics")} reqs={[rtlsReq]}>
        <h3 className={styles.classTitle}>{t("layers.class.analytics")}</h3>
        {layerRow("heatmap", t("layers.heatmap"))}
        {layerRow("spaghetti", t("layers.spaghetti"))}
        {layerRow("trail", t("layers.trail"))}
      </Reveal>

      {/* Every KPI badge is computed from a run's occupancy rollup, so the whole
          class — heading included — is meaningless before the first run. */}
      <Reveal label={t("layers.class.kpis")} reqs={[invReq, runsReq]}>
        <h3 className={styles.classTitle}>{t("layers.class.kpis")}</h3>
        {layerRow("kpiCoverage", t("layers.kpiCoverage"))}
        {layerRow("kpiOccupancy", t("layers.kpiOccupancy"))}
        {/* ADR 292: how full the used slots are, not how many are used. The row
            exists only when the run measured a height somewhere, and the accuracy
            row only when a WMS snapshot exists — a toggle for a badge that can
            only ever read "—" is a control bound to nothing (ADR 227). */}
        <Reveal label={t("layers.kpiVertical")} reqs={[verticalReq]}>
          {layerRow("kpiVertical", t("layers.kpiVertical"))}
        </Reveal>
        <Reveal label={t("layers.kpiAccuracy")} reqs={[wmsReq]}>
          {layerRow("kpiAccuracy", t("layers.kpiAccuracy"))}
        </Reveal>
      </Reveal>

      {/* ADR 354: what the occupancy paint ENCODES — one story at a time, so a
          mode control, not more checkboxes. Gated like the pallets row plus on
          the layer being on: with the paint hidden the mode controls nothing. */}
      <Reveal label={t("layers.paintBy")}
              reqs={[invReq, runsReq, wmsReq, { label: t("req.occupancyLayer"), ok: layers.occupancy }]}>
        <>
          <h3 className={styles.classTitle}>{t("layers.paintBy")}</h3>
          <div className={styles.preset} role="group" aria-label={t("layers.paintBy")}>
            <button type="button" aria-pressed={paintBy === "status"}
                    className={paintBy === "status" ? styles.btnActive : styles.btn}
                    onClick={() => setPaintBy("status")}>
              {t("layers.paintStatus")}
            </button>
            <button type="button" aria-pressed={paintBy === "expiry"}
                    className={paintBy === "expiry" ? styles.btnActive : styles.btn}
                    onClick={() => setPaintBy("expiry")}>
              {t("layers.paintExpiry")}
            </button>
            <button type="button" aria-pressed={paintBy === "aging"}
                    className={paintBy === "aging" ? styles.btnActive : styles.btn}
                    onClick={() => setPaintBy("aging")}>
              {t("layers.paintAging")}
            </button>
            {/* ADR 375: the WMS comparison on the floor. */}
            <button type="button" aria-pressed={paintBy === "wms"}
                    className={paintBy === "wms" ? styles.btnActive : styles.btn}
                    onClick={() => setPaintBy("wms")}>
              {t("layers.paintWms")}
            </button>
          </div>
          {/* The legend is mandatory while a ramp paints — color alone must
              never carry the reading. Rows are buttons: clicking one seeds the
              finder with the bucket's cumulative filter, which isolates the set
              on the scene and hands it to the simulator (ADR 351). The Idade
              ramp is the expiry ramp REVERSED (ADR 361): red is always "act". */}
          {/* ADR 375: the WMS legend carries no finder seed — the finder filters
              dates and text, and there is no "divergent" filter to hand it. Plain
              rows, so nothing advertises a click that does nothing. */}
          {paintBy === "wms" && paintCounts && (
            <div data-testid="wms-legend">
              {WMS_CLASSES.map((c, i) => (
                <div key={c.key} className={styles.row}>
                  <span aria-hidden style={{ width: 10, height: 10, borderRadius: 2, flexShrink: 0,
                        background: `var(--op-expiry-${c.ramp})` }} />
                  <span className={styles.label}>{t(`paint.wms.${c.key}`)}</span>
                  <span style={{ marginLeft: "auto", fontFamily: "var(--font-mono)", fontSize: 11 }}>
                    {paintCounts.buckets[i]}
                  </span>
                </div>
              ))}
              <div className={styles.row}>
                <span aria-hidden style={{ width: 10, height: 10, borderRadius: 2, flexShrink: 0, background: "var(--op-ink-500)" }} />
                <span className={styles.label}>{t("paint.wms.none")}</span>
                <span style={{ marginLeft: "auto", fontFamily: "var(--font-mono)", fontSize: 11 }}>
                  {paintCounts.noData}
                </span>
              </div>
            </div>
          )}
          {(paintBy === "expiry" || paintBy === "aging") && paintCounts && (
            <div data-testid={paintBy === "expiry" ? "expiry-legend" : "aging-legend"}>
              {(paintBy === "expiry" ? EXPIRY_BUCKETS : AGING_BUCKETS).map((b, i) => (
                <button key={b.key} type="button" className={styles.row}
                        style={{ width: "100%", background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}
                        title={t("paint.seedHint")}
                        onClick={() => {
                          const seed = paintBy === "expiry"
                            ? bucketFinderSeed(b.key as (typeof EXPIRY_BUCKETS)[number]["key"], todayIso)
                            : agingFinderSeed(b.key as (typeof AGING_BUCKETS)[number]["key"]);
                          if (seed) setProductFilters(seed);
                        }}>
                  <span aria-hidden style={{ width: 10, height: 10, borderRadius: 2, flexShrink: 0,
                        background: `var(--op-expiry-${paintBy === "expiry" ? i : AGING_BUCKETS.length - 1 - i})` }} />
                  <span className={styles.label}>{t(`paint.${b.key}`)}</span>
                  <span style={{ marginLeft: "auto", fontFamily: "var(--font-mono)", fontSize: 11 }}>
                    {paintCounts.buckets[i]}
                  </span>
                </button>
              ))}
              <div className={styles.row}>
                <span aria-hidden style={{ width: 10, height: 10, borderRadius: 2, flexShrink: 0, background: "var(--op-ink-500)" }} />
                <span className={styles.label}>{t(paintBy === "expiry" ? "paint.noData" : "paint.noFifo")}</span>
                <span style={{ marginLeft: "auto", fontFamily: "var(--font-mono)", fontSize: 11 }}>
                  {paintCounts.noData}
                </span>
              </div>
            </div>
          )}
        </>
      </Reveal>

      {/* ADR 198: run picker + movements — renders nothing until the warehouse
          has a second paintable run. */}
      {hasInventory && activeSiteId && activeWarehouseId && (
        <RunSelectionSection siteId={activeSiteId} warehouseId={activeWarehouseId} />
      )}

      {/* Labels annotate whatever else is on screen, so it is the one reference row
          with no data of its own to gate on. The class hides when nothing is left. */}
      {(available.zones || available.plan) && (
        <h3 className={styles.classTitle}>{t("layers.class.reference")}</h3>
      )}
      <Reveal label={t("layers.zones")} reqs={[{ label: t("req.zones"), ok: !!available.zones }]}>
        {layerRow("zones", t("layers.zones"))}
      </Reveal>
      {(available.zones || available.plan) && layerRow("labels", t("layers.labels"))}
      {/* The floor square under every position (the occupancy deck): a reference
          mark, toggled apart from the pallets it sits under. */}
      <Reveal label={t("layers.decks")} reqs={[invReq, runsReq, { label: t("req.occupancyLayer"), ok: layers.occupancy }]}>
        {layerRow("decks", t("layers.decks"))}
      </Reveal>
      <Reveal label={t("layers.minimap")} reqs={[{ label: t("req.plan"), ok: !!available.plan }]}>
        {layerRow("minimap", t("layers.minimap"))}
      </Reveal>

      <h2 className={styles.title} style={{ marginTop: "var(--space-4)" }}>{t("layers.camera")}</h2>
      <div className={styles.preset}>
        <button onClick={() => { setPreset("iso");   deselect(null); }} aria-pressed={preset === "iso"}   className={preset === "iso"   ? styles.btnActive : styles.btn}>{t("controls.preset.iso")}</button>
        <button onClick={() => { setPreset("top");   deselect(null); }} aria-pressed={preset === "top"}   className={preset === "top"   ? styles.btnActive : styles.btn}>{t("controls.preset.top")}</button>
        <button onClick={() => { setPreset("aisle"); deselect(null); }} aria-pressed={preset === "aisle"} className={preset === "aisle" ? styles.btnActive : styles.btn}>{t("controls.preset.aisle")}</button>
        <button onClick={() => { setPreset("dock");  deselect(null); }} aria-pressed={preset === "dock"}  className={preset === "dock"  ? styles.btnActive : styles.btn}>{t("controls.preset.dock")}</button>
      </div>
      {/* ADR 369: first-person walk — a mode, not a pose; it stays on until
          Esc, this button, or a preset. */}
      <button
        onClick={() => { setWalkMode(!walkMode); deselect(null); }}
        aria-pressed={walkMode}
        className={walkMode ? styles.btnActive : styles.btn}
        style={{ width: "100%", marginTop: 6 }}
      >
        {t("controls.walk.title")}
      </button>
      {walkMode && <p className={styles.hint}>{t("controls.walk.hint")}</p>}

      {/* ADR 395: saving the layout is a site-admin write; an account that cannot save it is
          not offered the editor at all. */}
      {canEditLayout && (<>
      <h2 className={styles.title} style={{ marginTop: "var(--space-4)" }}>{t("layers.layout")}</h2>
      {!editMode ? (
        <button className={styles.btn} style={{ width: "100%" }} onClick={handleToggleEdit}>
          {t("layers.editLayout")}
        </button>
      ) : (
        <div style={{ display: "flex", gap: 6 }}>
          <button className={styles.btnActive} style={{ flex: 1 }} onClick={handleSave}>
            {t("common.save")}
          </button>
          <button className={styles.btn} style={{ flex: 1 }} onClick={handleCancel}>
            {t("common.cancel")}
          </button>
        </div>
      )}
      </>)}
      </>)}
    </aside>
  );
}
